// Game companion play timer: start, pause and reset a timer, and count the hints and solutions opened
// (details[data-hint-kind] on the page, each counted once). State is saved per game (data-key) in
// localStorage. Renders into every matching control (section panel and floating pill).
class SGameTimer extends HTMLElement {
  #ac;
  #tick;
  #state = SGameTimer.#fresh();

  static #fresh() {
    return { running: false, startedAt: 0, elapsed: 0, hints: [], solutions: [] };
  }

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#load();
    this.addEventListener('command', (event) => this.#handle(event.command), { signal });
    // Fallback for browsers without invoker commands: same buttons, handled by click.
    if (!('CommandEvent' in window)) {
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button[command]');
        if (button) this.#handle(button.getAttribute('command'));
      }, { signal });
    }
    // toggle does not bubble, so listen in the capture phase for every hint on the page.
    document.addEventListener('toggle', (event) => this.#onHintToggle(event.target), { capture: true, signal });
    // The escape results card stops the clock; reload the saved state so the pill shows the final time.
    window.addEventListener('mysterylocks:timer-sync', () => {
      clearInterval(this.#tick);
      this.#load();
      this.#render();
    }, { signal });
    this.#render();
    if (this.#state.running) this.#startTicking();
    this.#watchPanel(signal);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearInterval(this.#tick);
  }

  get #storageKey() {
    return `mysterylocks-timer-${this.dataset.key || 'game'}`;
  }

  #load() {
    try {
      const saved = JSON.parse(localStorage.getItem(this.#storageKey));
      if (saved && typeof saved.elapsed === 'number') {
        this.#state = { ...SGameTimer.#fresh(), ...saved };
      }
    } catch (error) {
      // Private browsing or bad data: start fresh.
    }
  }

  #save() {
    try {
      localStorage.setItem(this.#storageKey, JSON.stringify(this.#state));
    } catch (error) {
      // Storage unavailable: the timer still works for this visit.
    }
  }

  #elapsedMs() {
    const { running, startedAt, elapsed } = this.#state;
    return running ? elapsed + (Date.now() - startedAt) : elapsed;
  }

  #onHintToggle(details) {
    if (!(details instanceof HTMLDetailsElement) || !details.open || this.#state.finished) return;
    const kind = details.dataset.hintKind;
    if (kind !== 'hint' && kind !== 'solution') return;
    const all = [...document.querySelectorAll('details[data-hint-kind]')];
    const index = all.indexOf(details);
    const list = kind === 'solution' ? this.#state.solutions : this.#state.hints;
    if (index < 0 || list.includes(index)) return;
    list.push(index);
    this.#save();
    this.#render();
  }

  #handle(command) {
    if (command === '--toggle') {
      if (this.#state.running) {
        this.#state = { ...this.#state, running: false, startedAt: 0, elapsed: this.#elapsedMs() };
        clearInterval(this.#tick);
      } else {
        this.#state = { ...this.#state, running: true, startedAt: Date.now() };
        this.#startTicking();
      }
    } else if (command === '--reset') {
      this.#state = SGameTimer.#fresh();
      clearInterval(this.#tick);
    } else {
      return;
    }
    this.#save();
    this.#render();
  }

  #startTicking() {
    clearInterval(this.#tick);
    this.#tick = setInterval(() => this.#renderClock(), 1000);
  }

  #setText(selector, text) {
    this.querySelectorAll(selector).forEach((node) => {
      if (node.textContent !== text) node.textContent = text;
    });
  }

  #renderClock() {
    const total = Math.floor(this.#elapsedMs() / 1000);
    const hours = Math.floor(total / 3600);
    const minutes = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
    const seconds = String(total % 60).padStart(2, '0');
    this.#setText('[data-timer-clock]', hours > 0 ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`);
  }

  #render() {
    this.#renderClock();
    const { running, elapsed, hints, solutions } = this.#state;
    const finished = Boolean(this.#state.finished);
    this.#setText('[data-hint-count]', String(hints.length));
    this.#setText('[data-solution-count]', String(solutions.length));
    this.querySelectorAll('[data-timer-toggle]').forEach((toggle) => {
      const kind = running ? 'labelPause' : elapsed > 0 ? 'labelResume' : 'labelStart';
      const label = toggle.dataset[kind] || this.dataset[kind];
      const text = toggle.querySelector('[data-timer-toggle-text]');
      if (text && text.textContent !== label) text.textContent = label;
      toggle.setAttribute('aria-pressed', running ? 'true' : 'false');
      if (toggle.hidden !== finished) toggle.hidden = finished;
      toggle.querySelector('[data-icon-play]')?.toggleAttribute('hidden', running);
      toggle.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', !running);
    });
    const untouched = !running && elapsed === 0 && hints.length === 0 && solutions.length === 0;
    this.querySelectorAll('[data-timer-reset]').forEach((reset) => {
      if (reset.hidden !== untouched) reset.hidden = untouched;
    });
    const note = this.querySelector('[data-timer-finished]');
    if (note && note.hidden === finished) note.hidden = !finished;
  }

  // Floating pill shows only while the timer section is out of view.
  #watchPanel(signal) {
    const panel = this.querySelector('[data-timer-panel]');
    const float = this.querySelector('[data-timer-float]');
    if (!panel || !float || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting;
      if (float.hasAttribute('data-show') === show) return;
      float.toggleAttribute('data-show', show);
      float.toggleAttribute('inert', !show);
      float.setAttribute('aria-hidden', show ? 'false' : 'true');
    });
    observer.observe(panel);
    signal.addEventListener('abort', () => observer.disconnect());
  }
}

if (!customElements.get('s-game-timer')) customElements.define('s-game-timer', SGameTimer);
